import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, Skeleton, Badge, Empty, ErrorBox } from "@/components/ui";
import type { Task, TaskCommunicationDraft } from "@/types";

type Group = { key: string; patientId?: string | null; patientName: string; patientMrn: string; tasks: Task[] };

function groupTasks(rows: Task[]): Group[] {
  const map = new Map<string, Group>();
  for (const t of rows) {
    const key = t.patient_id || "unassigned";
    if (!map.has(key)) map.set(key, { key, patientId: t.patient_id, patientName: t.patient_name || "Unassigned / non-patient action", patientMrn: t.patient_mrn || "—", tasks: [] });
    map.get(key)!.tasks.push(t);
  }
  return [...map.values()];
}

function supportsDrafts(t: Task): boolean {
  const category = (t.category || "").toLowerCase();
  const text = `${t.title || ""} ${t.description || ""}`.toLowerCase();
  return ["appointment", "case_manager", "report_request", "coordination"].includes(category)
    || ["appointment", "case manager", "coordinator", "follow-up", "follow up", "arrange"].some((needle) => text.includes(needle));
}

function draftLabel(d: TaskCommunicationDraft): string {
  if (d.draft_type === "email") return "Email draft";
  if (d.draft_type === "whatsapp") return "SMS / WhatsApp draft";
  return `${d.draft_type} draft`;
}

function TaskDrafts({ task }: { task: Task }) {
  const qc = useQueryClient();
  const queryKey = ["task-communication-drafts", task.id];
  const drafts = useQuery({
    queryKey,
    queryFn: () => api.get<TaskCommunicationDraft[]>(`/tasks/${task.id}/communication-drafts`),
    enabled: supportsDrafts(task),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ["tasks-global"] });
  };
  const generate = useMutation({ mutationFn: () => api.post<TaskCommunicationDraft[]>(`/tasks/${task.id}/communication-drafts/generate`, {}), onSuccess: invalidate });
  const save = useMutation({ mutationFn: ({ draftId, body }: { draftId: string; body: Partial<TaskCommunicationDraft> }) => api.patch<TaskCommunicationDraft>(`/tasks/${task.id}/communication-drafts/${draftId}`, body), onSuccess: invalidate });
  const copied = useMutation({ mutationFn: (draftId: string) => api.post<TaskCommunicationDraft>(`/tasks/${task.id}/communication-drafts/${draftId}/copied`, {}), onSuccess: invalidate });
  const sent = useMutation({ mutationFn: (draftId: string) => api.post<TaskCommunicationDraft>(`/tasks/${task.id}/communication-drafts/${draftId}/sent-by-user`, {}), onSuccess: invalidate });

  if (!supportsDrafts(task) || task.status === "closed" || task.status === "cancelled") return null;

  return (
    <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/40 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-200">Communication drafts</div>
          <div className="text-xs text-slate-500">Copyable/editable only. RPIS does not send email, SMS, or WhatsApp.</div>
        </div>
        <button className="btn-ghost" disabled={generate.isPending} onClick={() => generate.mutate()}>
          {drafts.data?.length ? "Refresh drafts" : "Generate drafts"}
        </button>
      </div>
      {drafts.error ? <ErrorBox error={drafts.error} /> : null}
      {drafts.isLoading ? <Skeleton /> : null}
      {(drafts.data || []).map((d) => (
        <DraftEditor key={d.id} task={task} draft={d} onCopy={async () => {
          const text = [d.subject, d.body].filter(Boolean).join("\n\n");
          await navigator.clipboard.writeText(text);
          copied.mutate(d.id);
        }} onSave={(body) => save.mutate({ draftId: d.id, body })} onSent={() => sent.mutate(d.id)} />
      ))}
    </div>
  );
}

function DraftEditor({ task, draft, onCopy, onSave, onSent }: { task: Task; draft: TaskCommunicationDraft; onCopy: () => void; onSave: (body: Partial<TaskCommunicationDraft>) => void; onSent: () => void }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const canSend = task.status !== "closed" && task.status !== "cancelled";
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-medium text-slate-100">{draftLabel(draft)}</div>
        <div className="flex gap-2 flex-wrap">
          <Badge tone={draft.status === "sent_by_user" ? "ok" : draft.status === "copied" ? "info" : "muted"}>{draft.status}</Badge>
          {draft.sent_by_user_at ? <Badge tone="muted">Sent: {draft.sent_by_user_at.slice(0, 10)}</Badge> : null}
        </div>
      </div>
      {draft.draft_type === "email" ? (
        <div>
          <label className="label">Subject</label>
          {editing ? <input className="input w-full" value={subject} onChange={(e) => setSubject(e.target.value)} /> : <div className="text-sm text-slate-300">{draft.subject || "—"}</div>}
        </div>
      ) : null}
      <div>
        <label className="label">Message</label>
        {editing ? <textarea className="input min-h-40 w-full" value={body} onChange={(e) => setBody(e.target.value)} /> : <div className="text-sm text-slate-300 whitespace-pre-wrap">{draft.body || "—"}</div>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button className="btn-ghost" onClick={onCopy}>Copy {draft.draft_type === "email" ? "email draft" : "SMS / WhatsApp draft"}</button>
        {editing ? (
          <button className="btn-primary" onClick={() => { onSave({ subject, body }); setEditing(false); }}>Save edit</button>
        ) : (
          <button className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>
        )}
        {canSend ? <button className="btn-primary" onClick={onSent}>Mark as sent / I sent it</button> : null}
      </div>
    </div>
  );
}

export default function Tasks() {
  const [status, setStatus] = useState("open");
  const [overdue, setOverdue] = useState(false);
  const qc = useQueryClient();
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (overdue) qs.set("overdue", "true");
  const q = useQuery({ queryKey: ["tasks-global", qs.toString()], queryFn: () => api.get<Task[]>(`/tasks?${qs.toString()}`) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["tasks-global"] });
  const close = useMutation({ mutationFn: ({ id, note }: { id: string; note: string }) => api.post(`/tasks/${id}/close`, { resolution_note: note }), onSuccess: invalidate });
  const cancel = useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/tasks/${id}/cancel`, { reason }), onSuccess: invalidate });
  const edit = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<Task> }) => api.patch(`/tasks/${id}`, body), onSuccess: invalidate });
  const groups = useMemo(() => groupTasks(q.data || []), [q.data]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-50">Clinical Action Center</h1>
        <div className="text-sm text-slate-400">Grouped real actionable work. Routine clinical reminders belong in the patient Next Visit Suggested Note.</div>
      </div>
      <Card>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="label">Status</label>
            <select className="input w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
              {["","open","in_progress","waiting_external","waiting_patient","closed","cancelled"].map(s => <option key={s} value={s}>{s || "any"}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={overdue} onChange={(e) => setOverdue(e.target.checked)} /> Overdue only</label>
        </div>
      </Card>
      {q.error ? <ErrorBox error={q.error} /> : null}
      {q.isLoading ? <Card><Skeleton /></Card> : !groups.length ? <Empty title="No action items." /> : groups.map((g) => (
        <Card key={g.key} title={`${g.patientName} · MRN ${g.patientMrn}`} actions={g.patientId ? <Link className="btn-ghost" to={`/patients/${g.patientId}`}>View Patient</Link> : null}>
          <div className="space-y-3">
            {g.tasks.map((t) => (
              <div key={t.id} className="card-tight">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium text-slate-100">{t.title}</div>
                    {t.description ? <div className="text-sm text-slate-400 whitespace-pre-wrap mt-1">{t.description}</div> : null}
                    <div className="text-xs text-slate-500 mt-2">Source: {t.source || "legacy/manual"} · Category: {t.category || "—"}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    <Badge tone={t.status === "closed" ? "ok" : t.status === "cancelled" ? "muted" : "info"}>{t.status}</Badge>
                    <Badge tone={(t.urgency || t.priority) === "urgent" ? "bad" : (t.urgency || t.priority) === "high" ? "warn" : "muted"}>Urgency: {t.urgency || t.priority}</Badge>
                    <Badge tone="muted">Action Date: {t.action_date || t.due_date || "—"}</Badge>
                  </div>
                </div>
                <TaskDrafts task={t} />
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button className="btn-ghost" onClick={() => {
                    const title = prompt("Edit title", t.title);
                    if (title !== null) edit.mutate({ id: t.id, body: { title } });
                  }}>Edit</button>
                  <button className="btn-ghost" onClick={() => {
                    const due = prompt("New action date (YYYY-MM-DD)", t.action_date || t.due_date || "");
                    if (due !== null) edit.mutate({ id: t.id, body: { due_date: due || null, action_date: due || null } });
                  }}>Snooze/change action date</button>
                  {t.status !== "closed" && t.status !== "cancelled" ? <>
                    <button className="btn-primary" onClick={() => close.mutate({ id: t.id, note: prompt("Resolution note", "Handled / resolved.") || "Handled / resolved." })}>Close with resolution note</button>
                    <button className="btn-danger" onClick={() => cancel.mutate({ id: t.id, reason: prompt("Cancellation reason", "No longer needed.") || "No longer needed." })}>Cancel with reason</button>
                  </> : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
